import { SendOutlined } from "@ant-design/icons";
import { Avatar, Button, Col, Form, Input, Modal, Row } from "antd";
import type React from "react";
import {
	useChats,
	useMemberDetail,
	useSendChat,
} from "../hooks/useEventDetail";
import type { ChatMessage } from "../types";
import { toJSTDayjs } from "../utils";

interface ChatModalProps {
	open: boolean;
	onClose: () => void;
	memberId: number;
	memberName: string;
	primaryColor?: string;
}

export const ChatModal: React.FC<ChatModalProps> = ({
	open,
	onClose,
	memberId,
	memberName,
	primaryColor = "#99CA29",
}) => {
	const [form] = Form.useForm();

	const { data: member } = useMemberDetail(open ? memberId : undefined);
	const { data: chats = [], isLoading } = useChats(
		open ? memberId : undefined,
		open,
	);
	const { mutate: sendChat, isPending: isSending } = useSendChat();

	const handleSend = (values: { contents: string }) => {
		sendChat(
			{ memberId, contents: values.contents },
			{
				onSuccess: () => {
					form.resetFields();
				},
			},
		);
	};

	return (
		<Modal
			title={`${memberName}様`}
			open={open}
			onCancel={onClose}
			footer={null}
			centered
			destroyOnClose
		>
			<div
				style={{
					height: "60vh",
					overflowY: "auto",
					paddingBottom: "16px",
					display: "flex",
					flexDirection: "column",
					gap: "8px",
				}}
			>
				{isLoading ? (
					<div style={{ textAlign: "center", padding: "24px" }}>
						読み込み中...
					</div>
				) : (
					chats.map((chat: ChatMessage) => {
						const isUser = chat.source === "user";
						return (
							<Row key={chat.chatId} style={{ marginTop: "8px" }}>
								<Col span={22} offset={isUser ? 0 : 2}>
									{isUser ? (
										<div>
											<div
												style={{ display: "flex", alignItems: "flex-start" }}
											>
												<Avatar
													src={
														member?.picUrl
															? `${member.picUrl}/small`
															: "/no-image.png"
													}
													style={{ marginRight: "8px" }}
												/>
												{chat.contentType === "text" && (
													<div
														style={{
															backgroundColor: "#21acd7",
															color: "white",
															padding: "8px",
															borderRadius: "8px",
															whiteSpace: "pre-wrap",
														}}
													>
														{chat.contents}
													</div>
												)}
											</div>
											<div
												style={{
													fontSize: "10px",
													color: "#9ca3af",
													marginLeft: "40px",
													marginTop: "4px",
												}}
											>
												{chat.createdAt
													? toJSTDayjs(chat.createdAt).format(
															"YYYY年M月D日 HH:mm",
														)
													: ""}
											</div>
										</div>
									) : (
										<div
											style={{
												display: "flex",
												flexDirection: "column",
												alignItems: "flex-end",
											}}
										>
											{chat.contentType === "text" && (
												<div
													style={{
														backgroundColor: primaryColor,
														color: "white",
														padding: "8px",
														borderRadius: "8px",
														whiteSpace: "pre-wrap",
													}}
												>
													{chat.contents}
												</div>
											)}
											<div
												style={{
													fontSize: "10px",
													color: "#9ca3af",
													marginTop: "4px",
												}}
											>
												{chat.createdAt
													? toJSTDayjs(chat.createdAt).format(
															"YYYY年M月D日 HH:mm",
														)
													: ""}
											</div>
										</div>
									)}
								</Col>
							</Row>
						);
					})
				)}
			</div>

			<div
				style={{
					borderTop: `2px solid ${primaryColor}`,
					paddingTop: "16px",
					marginTop: "8px",
				}}
			>
				<Form form={form} onFinish={handleSend}>
					<div
						style={{
							display: "flex",
							justifyContent: "flex-end",
							marginBottom: "8px",
						}}
					>
						<Button
							type="primary"
							htmlType="submit"
							icon={<SendOutlined />}
							loading={isSending}
							style={{
								backgroundColor: primaryColor,
								borderColor: primaryColor,
							}}
						/>
					</div>
					<Form.Item
						name="contents"
						rules={[
							{
								required: true,
								whitespace: true,
								message: "メッセージを入力してください",
							},
						]}
					>
						<Input.TextArea
							autoSize={{ minRows: 4, maxRows: 8 }}
							placeholder="メッセージを入力してください"
						/>
					</Form.Item>
				</Form>
			</div>
		</Modal>
	);
};
